/**
 * Created by Carsten Hibbeler on 07.02.2016.
 */

'use strict';

import React from 'external/react';
import PropTypes from 'external/prop-types';
import { AppContext, DataUtil, FlagComponent, PluginComponent, UidUtil } from 'plugin/liferay-widgets-react-web';
import MapConfigPlugin from '../plugins/MapConfigPlugin';
import LeafletLayerManagerPlugin from '../plugins/LeafletLayerManagerPlugin';
import DefaultPosition from '../plugins/DefaultPosition';
import MapScalePlugin from '../plugins/MapScalePlugin';
import SelectionDisplayPlugin from '../plugins/SelectionDisplayPlugin';
import { getMapConfigStore } from '../stores/StoreFactory';
import L from 'external/leaflet';
import { GestureHandling } from 'leaflet-gesture-handling';
import '../css/map.scss';
import 'leaflet-gesture-handling/dist/leaflet-gesture-handling.css';
import '../utils/leaflet-control-positions';
import { FliwasCanvasRenderer } from '../FeatureRenderer/FliwasCanvasRenderer';
import '../plugins/ToastPlugin';

const Leaflet = L; //this is a hack because we use a global Leaflet
/*
FLIWASEN-932
Canvas layers don't propagate events to layers below in the layer stack. Even after removing
the layer leaflet keeps the empty canvas in the layer pane.
We extend the map to keep track of all added canvas layers by zIndex and forward events to
layers positioned below.
Check FliwasCanvasRenders _onClick and _handleMouseOver to see how events are forwarded
 */
L.Map.include({
    // object that stores arrays of canvas renderers by zIndex
    _canvasRenderers: {},
    _registerCanvasRenderer: function (layer) {
        const zIndex = layer.options.zIndex;
        if (this._canvasRenderers[ zIndex ]) {
            this._canvasRenderers[ zIndex ].push(layer);
        } else {
            this._canvasRenderers[ zIndex ] = [ layer ];
        }
    },

    _unregisterCanvasRenderer: function (layer) {
        const zIndex = layer.options.zIndex;
        if (this._canvasRenderers[ zIndex ]) {
            this._canvasRenderers[ zIndex ] = this._canvasRenderers[ zIndex ].filter(r => r !== layer);
            if (this._canvasRenderers[ zIndex ].length === 0) {
                delete this._canvasRenderers[ zIndex ];
            }
        }
    },

    _forwardCanvasEvent: function (layer, e) {
        const latLng = this.mouseEventToLatLng(e);
        /*
        Projects a geographical coordinate LatLng according to the projection of the map's CRS,
        then scales it according to zoom and the CRS's transformation.
        The result is pixel coordinate relative to the CRS origin which gets divided by tile size.
         */
        let tileCoord = ((this.project(latLng, this.getZoom()).floor()).unscaleBy(L.point(256, 256)).floor());
        tileCoord.z = this.getZoom();

        // 1000: fliwas object layers
        const zIndex = layer.options.zIndex || 1000;
        let nextZIndex = -1;
        Object.keys(this._canvasRenderers).forEach(key => {
            if (key < zIndex && key > nextZIndex) {
                nextZIndex = key;
            }
        });
        const forwardRenderers = this._canvasRenderers[ nextZIndex ];

        if (forwardRenderers) {
            // forward to the renderer *below* the renderer the originally received event
            forwardRenderers
                .filter(r => {
                    if (r._bounds) {
                        return r._bounds.contains(latLng);
                    }
                    return r._tileCoord.equals(tileCoord);
                })
                .forEach(r => {
                    r._dispatchEvent && r._dispatchEvent(e);
                });
        }
    }
});

L.Map.addInitHook('addHandler', 'gestureHandling', GestureHandling);

//Bugfix FLIWAS-1052
L.Path.addInitHook(function () {
    this._onClick = this._fireMouseEvent;
});

const isOverflown = element => element.scrollHeight > element.clientHeight;
const isFullscreen = () => document.isFullscreen || document.webkitIsFullScreen || document.mozFullScreen || document.msFullscreenElement || document.body.classList.contains('map-fullscreen');

const MapConfigStore = getMapConfigStore(AppContext);

class Map extends PluginComponent {

    constructor(props) {
        super(props, props.plugins);

        const mapKey = props.mapKey || UidUtil.generateId('map');

        this.state = {
            ...this.state,
            mapKey
        };

        // not using state to prevent plugin updates and re-render
        this.overflow = false;
        this.scrollOverlay = null;
        this.isFullscreen = null;

        this.initDefaultPlugins();
        this.onResize = this.onResize.bind(this);
    }

    initDefaultPlugins() {
        console.log('Map initDefaultPlugins()');
        this.defaultPluginDefaultPosition = this.asPlugin(DefaultPosition);
        this.defaultPluginMapConfigPlugin = this.asPlugin(MapConfigPlugin);
        this.defaultPluginLeafletLayerManagerPlugin = this.asPlugin(LeafletLayerManagerPlugin);
        this.defaultPluginMapScalePlugin = this.asPlugin(MapScalePlugin);
        this.defaultPluginSelectionDisplayPlugin = this.asPlugin(SelectionDisplayPlugin);
        Object.keys(this)
            .filter(k => k.indexOf('defaultPlugin') === 0 && ((this[ k ].staticPlugin && !this[ k ].staticPlugin) || (this[ k ].constructor && !this[ k ].constructor.staticPlugin)))
            .forEach(p => console.error('Default plugin is not static', p));
    }

    onPluginsChanged(newPlugins) {
        super.onPluginsChanged(newPlugins);
        this.initDefaultPlugins();
    }

    componentWillUnmount() {
        if (this.map) {
            this.map.off();
            this.map.remove();
        }
        super.componentWillUnmount();
    }

    UNSAFE_componentWillReceiveProps(nextProps) {
        const mapKey = nextProps.mapKey || this.state.mapKey || UidUtil.generateId('map');
        if (mapKey !== this.state.mapKey) {
            this.setState({
                mapKey
            });
        }

        super.UNSAFE_componentWillReceiveProps(nextProps);
    }

    plugins() {
        return [
            this.defaultPluginDefaultPosition,
            this.defaultPluginMapConfigPlugin,
            ...super.plugins(),
            this.defaultPluginLeafletLayerManagerPlugin,
            this.defaultPluginMapScalePlugin,
            this.defaultPluginSelectionDisplayPlugin,
        ];
    }

    //React update is finished. Init or update the map
    initWithPlugins(o) {
        console.log('Init map! options: ', o, ' plugins: ', this.plugins());
        o.maxZoom = o.maxZoom || 18;
        this.map = Leaflet.map(this.state.mapKey, {
            ...o,
            maxZoom: 18,
            preferCanvas: true,
            renderer: new FliwasCanvasRenderer(),
            dragging: !L.Browser.mobile,
            tap: !L.Browser.mobile,
            gestureHandling: false,
            gestureHandlingText: {
                touch: 'Verschieben der Karte mit zwei Fingern',
                scroll: 'Verwenden Sie Strg + Scrollen zum Zoomen der Karte',
                scrollMac: 'Verwenden Sie \u2318 + Scrollen zum Zoomen der Karte'
            }
        });
        this.map.whenReady(() => this.map.gestureHandling._handleMouseOver());

        // access with map.toast
        L.control.toast({
                timeout: 3000,
                position: 'bottomcenter',
                closable: true,
                dismissable: true,
            })
            .addTo(this.map);

        this.map.attributionControl.setPrefix('&copy; Leaflet');
        this.map.attributionControl.addAttribution('&copy; 2024 <a href="https://maps.omniscale.com/en/" target="_blank">maps.omniscale.com</a>, Kartendaten: <a href="https://www.openstreetmap.org/copyright" target="_blank">OpenStreetMap</a> - (Lizenz ODbL)');
        this.map.sideBars = []; // store all sidebars here to close them if another ones opens
        this.map.blockingControlActive = false; // flag to prevent certain actions while a blocking control is active

        this.isFullscreen = isFullscreen();
        Array.prototype.forEach.call(document.getElementsByClassName('modal-body'), (elem) => {
            if (elem.contains(this.map._container)) {
                this.modal = elem;
            }
        });

        this.map.on('resize', this.onResize);

        this.updateMap(this.map, o);
        if (__DEV__) {
            o = Object.freeze(o);
        }
        this.callPluginsInit(this.map, o);
    }

    reinitWithPlugins(o) {
        console.log('Reinit map! options: ', o, ' plugins: ', this.plugins());
        if (this.map) {
            this.map.remove();
        }

        this.initWithPlugins(o);
    }

    updateWithPlugins(o) {
        this.updateMap(this.map, o);
        this.callPluginsUpdate(this.map, o);
    }

    updateMap(map, o) {
        if (o && o.boundRequest && !o.boundRequest.handled) {
            const { bounds } = o.boundRequest;
            if (bounds && bounds.getNorthEast().equals(bounds.getSouthWest())) {
                if (!map.getZoom()) {
                    map.setZoom(10);
                }
                map.panTo(o.boundRequest.bounds.getCenter(), o.boundRequest.options);
            } else {
                if (bounds) {
                    map.fitBounds(o.boundRequest.bounds, o.boundRequest.options);
                }
            }
            o.boundRequest.cb();
            o.boundRequest.handled = true;
        }

        this.onResize();
    }

    onResize() {
        this.isFullscreen = isFullscreen();
        this.overflow = isOverflown(this.modal || document.body);
        if (this.overflow && !this.isFullscreen) {
            this.map && this.map.gestureHandling.enable();
        } else {
            this.map && this.map.gestureHandling.disable();
        }
    }

    render() {

        return (
            <div
                ref={ div => this.container = div }
                id={ this.state.mapKey }
                style={ {
                    height: '350px',
                    width: '100%',
                    ...this.props.style
                } }
                className={ this.props.className }
            >
                { this.state.loadPlugins && <span>Plugins werden geladen</span> }
            </div>
        );
    }
}

function MapConfigWrapper(props) {
    const { plugins = [], defaultFallbackMapConfigType = 'default' } = props,
        dataMapConfig = props.mapConfig || (defaultFallbackMapConfigType === 'default' ? MapConfigStore.getDefaultMapConfig() : MapConfigStore.getFallbackMapConfig()),
        mapConfig = DataUtil.asItem(dataMapConfig),
        mapConfigPlugins = mapConfig && mapConfig.plugins || [];

    return (
        <FlagComponent item={ mapConfig }>
            <Map
                { ...props }
                mapConfig={ dataMapConfig }
                plugins={ mapConfigPlugins.concat(plugins) }
            />
        </FlagComponent>
    );
}

Map.contextTypes = {
    contextId: PropTypes.string
};

Map.propTypes = {
    ...PluginComponent.propTypes,
    mapKey: PropTypes.string,
    style: PropTypes.object,
    selectedItems: PropTypes.array,
    disabledItems: PropTypes.array,
};

MapConfigWrapper.propTypes = {
    plugins: PropTypes.array,
    defaultFallbackMapConfigType: PropTypes.oneOf([ 'default', 'static' ]),
    mapConfig: PropTypes.object
};

export default MapConfigWrapper;



// WEBPACK FOOTER //
// ./js/components/Map.jsx